import { useState } from "react";
import { LifeBuoy, Mail, Truck, RotateCcw, Package, Send } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";

const TOPICS = [
  { icon: Package, title: "Order Support", text: "Questions about an order you've placed or received." },
  { icon: Truck, title: "Shipping Help", text: "Delivery times, pincodes and same-day availability." },
  { icon: RotateCcw, title: "Returns & Refunds", text: "Damaged gift? We replace or refund within 7 days." },
];

export default function SupportPage() {
  const [form, setForm] = useState({ name: "", email: "", order_id: "", issue: "Order Support", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/support", form);
      setDone(data);
      toast.success("Message sent!");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12 lg:py-16" data-testid="support-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">Need Help?</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 mt-3 tracking-tight">We're here, always.</h1>
      <p className="text-stone-500 mt-2 max-w-lg">Real humans, quick answers. Reach us any way you like.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
        {TOPICS.map((t) => (
          <div key={t.title} className="p-6 rounded-2xl bg-white border border-[#E9E2D7] card-lift">
            <t.icon size={22} className="text-[#B45309]" />
            <h3 className="font-bold text-stone-800 mt-3 text-sm">{t.title}</h3>
            <p className="text-sm text-stone-500 mt-1.5">{t.text}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 mt-10">
        <div className="bg-white rounded-2xl border border-[#E9E2D7] p-6 sm:p-8">
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10" data-testid="support-confirmation">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#78866B]/15 grid place-items-center"><Send size={26} className="text-[#5c6b52]" /></div>
              <h2 className="font-serif text-2xl font-bold text-stone-900 mt-5">Message received!</h2>
              <p className="text-stone-500 mt-2 max-w-sm mx-auto">{done.message}</p>
              <p className="text-xs text-stone-400 mt-3">Ticket ID: <span className="font-mono font-bold">{done.ticket_id}</span></p>
              <button onClick={() => { setDone(null); setForm({ name: "", email: "", order_id: "", issue: "Order Support", message: "" }); }} className="mt-6 px-6 h-11 rounded-full border border-stone-300 text-sm font-semibold text-stone-600" data-testid="support-another">Send another message</button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="support-form">
              <h2 className="font-serif text-xl font-bold text-stone-900">Write to us</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className="h-12 px-4 rounded-xl border border-stone-200 text-sm outline-none focus:border-[#B45309]" data-testid="support-name" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" className="h-12 px-4 rounded-xl border border-stone-200 text-sm outline-none focus:border-[#B45309]" data-testid="support-email" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input value={form.order_id} onChange={set("order_id")} placeholder="Order ID (optional, e.g. GFT123ABC)" className="h-12 px-4 rounded-xl border border-stone-200 text-sm outline-none focus:border-[#B45309]" data-testid="support-order-id" />
                <select value={form.issue} onChange={set("issue")} className="h-12 px-4 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#B45309]" data-testid="support-issue">
                  {["Order Support", "Shipping Help", "Returns & Refunds", "Payment Issue", "Something Else"].map((i) => <option key={i}>{i}</option>)}
                </select>
              </div>
              <textarea required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder="Tell us what's on your mind…" className="w-full px-4 py-3 rounded-xl border border-stone-200 text-sm outline-none focus:border-[#B45309] resize-none" data-testid="support-message" />
              <button disabled={busy} className="h-12 px-8 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] transition-colors disabled:opacity-60" data-testid="support-submit">
                {busy ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </div>
        <div className="space-y-4">
          <div className="p-6 rounded-2xl bg-[#1C1917] text-[#FDFBF7]">
            <Mail size={20} className="text-[#E0A96D]" />
            <h3 className="font-serif text-lg font-bold mt-3">Talk to us</h3>
            <p className="text-sm text-stone-400 mt-1">customizegiftonline@gmail.com</p>
            <p className="text-sm text-stone-400">9899534126</p>
            <p className="text-xs text-stone-500 mt-2">Replies within 24 hours, usually much faster.</p>
          </div>
          <div className="p-6 rounded-2xl bg-[#F9F3EA] border border-[#E9E2D7]">
            <LifeBuoy size={20} className="text-[#B45309]" />
            <h3 className="font-serif text-lg font-bold text-stone-900 mt-3">Help Center hours</h3>
            <p className="text-sm text-stone-500 mt-1">Monday – Saturday<br />9:00 AM – 9:00 PM IST</p>
          </div>
        </div>
      </div>
    </div>
  );
}
